import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  List,
  Card,
  Row,
  Col,
  Input,
  Select,
  Button,
  Space,
  Spin,
  message,
  Empty,
  Grid,
  Tag,
} from "antd";
import {
  ReloadOutlined,
  PhoneOutlined,
  FilterOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const getStoredToken = () => {
  const keys = ["token", "accessToken", "AuthToken", "jwtToken"];
  for (const k of keys) {
    const v = localStorage.getItem(k);
    if (v) return v.replace(/^"|"$/g, "");
  }
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user?.token || user?.accessToken || null;
  } catch {
    return null;
  }
};

const api = axios.create({
  baseURL: global.config.APIURL.BaseURL,
  timeout: 15000,
});

api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers = {
      ...(config.headers || {}),
      Authorization: `Bearer ${token}`,
    };
  }
  if (config.withCredentials === undefined) {
    config.withCredentials = false;
  }
  return config;
});

const DirectRegistrationRequests = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);

  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState(undefined);
  const [visibleCount, setVisibleCount] = useState(10);

  useEffect(() => {
    fetchUsers(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async (initial = false) => {
    try {
      if (initial) setLoading(true);
      else setFetching(true);

      const res = await api.get("api/HighIntentLeads/GetDirectRegisteredUsers");
      const data = Array.isArray(res.data) ? res.data : [];
      setUsers(data);
      setVisibleCount(isMobile ? 10 : 20);
    } catch (err) {
      if (err?.response) {
        if (err.response.status === 401) {
          messageApi.error("Unauthorized. Please log in again.");
        } else {
          messageApi.error(`Failed to fetch users (${err.response.status}).`);
        }
        console.error("API error:", err.response);
      } else if (err?.request) {
        messageApi.error(
          "Network/CORS error. Check SSL, CORS, or ad-blockers."
        );
        console.error("Request error (no response):", err.request);
      } else {
        messageApi.error("Unexpected error occurred.");
        console.error("Unexpected error:", err.message);
      }
    } finally {
      setLoading(false);
      setFetching(false);
    }
  };

  const stateOptions = useMemo(() => {
    const set = new Set(users.map((l) => l.State).filter(Boolean));
    return Array.from(set).map((v) => ({ label: v, value: v }));
  }, [users]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((l) => {
      const matchesQuery =
        !q ||
        [l.Name, l.ContactNo, l.City, l.State, l.PinCode]
          .filter(Boolean)
          .some((x) => String(x).toLowerCase().includes(q));
      const matchesState = !stateFilter || l.State === stateFilter;
      return matchesQuery && matchesState;
    });
  }, [users, search, stateFilter]);

  const visibleUsers = useMemo(
    () => filtered.slice(0, visibleCount),
    [filtered, visibleCount]
  );

  const resetFilters = () => {
    setSearch("");
    setStateFilter(undefined);
    setVisibleCount(isMobile ? 10 : 20);
  };

  const loadMore = () => {
    setVisibleCount((c) => c + (isMobile ? 10 : 20));
  };

  const openDialer = (phone) => {
    if (!phone) return;
    window.location.href = `tel:${phone}`;
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f5f6fa",
        padding: isMobile ? "8px" : "16px",
      }}
    >
      {contextHolder}

      {/* Sticky toolbar */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          background: "#f5f6fa",
          paddingBottom: 8,
        }}
      >
        <Row gutter={[8, 8]} align="middle" justify="space-between">
          <Col flex="auto">
            <Title
              level={isMobile ? 4 : 3}
              style={{ margin: 0, color: "#1890ff", fontWeight: 700 }}
            >
              Direct Registered Users
            </Title>
            <Text type="secondary" style={{ fontSize: isMobile ? 12 : 14 }}>
              {filtered.length} user{filtered.length === 1 ? "" : "s"}
            </Text>
          </Col>
          <Col>
            <Button
              type="default"
              icon={<ReloadOutlined />}
              onClick={() => fetchUsers(false)}
              loading={fetching}
              size={isMobile ? "middle" : "large"}
            >
              Refresh
            </Button>
          </Col>
        </Row>

        <Card
          size="small"
          style={{
            marginTop: 8,
            borderRadius: 10,
            border: "1px solid #e6f7ff",
          }}
          bodyStyle={{ padding: isMobile ? 8 : 12 }}
        >
          <Space direction="vertical" style={{ width: "100%" }} size={8}>
            <Input
              allowClear
              placeholder="Search name, phone, city, state, pincode..."
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setVisibleCount(isMobile ? 10 : 20);
              }}
              size={isMobile ? "middle" : "large"}
            />
            <Row gutter={[8, 8]}>
              <Col xs={24}>
                <Select
                  allowClear
                  placeholder="State"
                  options={stateOptions}
                  value={stateFilter}
                  onChange={(v) => {
                    setStateFilter(v);
                    setVisibleCount(isMobile ? 10 : 20);
                  }}
                  style={{ width: "100%" }}
                  size={isMobile ? "middle" : "large"}
                  suffixIcon={<FilterOutlined />}
                />
              </Col>
            </Row>
            {(search || stateFilter) && (
              <Button
                onClick={resetFilters}
                size={isMobile ? "middle" : "large"}
              >
                Clear filters
              </Button>
            )}
          </Space>
        </Card>
      </div>
      {loading ? (
        <div style={{ textAlign: "center", marginTop: 40 }}>
          <Spin size="large" />
        </div>
      ) : filtered.length === 0 ? (
        <Card
          style={{
            marginTop: 12,
            borderRadius: 12,
            border: "1px solid #f0f0f0",
          }}
        >
          <Empty description="No users found" />
        </Card>
      ) : (
        <List
          grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 3, xl: 4, xxl: 4 }}
          style={{ marginTop: 12 }}
          dataSource={visibleUsers}
          renderItem={(item) => {
            const name = item?.Name || "Unknown";
            const phone = item?.ContactNo || "";
            const city = item?.City || "-";
            const state = item?.State || "-";
            const pinCode = item?.PinCode || "-";
            const isAlreadyRegister = item?.IsAlreadyRegister;
            const isActiveUser = item?.IsActiveUser;
            const dateStr = item?.RequestedOn
              ? moment(item.RequestedOn).format("DD MMM YYYY, hh:mm A")
              : "-";

            return (
              <List.Item>
                <Card
                style={{
                  marginBottom: 12,
                  borderRadius: 12,
                  border: "1px solid #e6f7ff",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  cursor: "default",
                }}
                bodyStyle={{ padding: isMobile ? 12 : 16 }}
              >
                <Row gutter={[8, 8]} align="middle">
                  <Col xs={24}>
                    <Space
                      direction="horizontal"
                      align="center"
                      style={{ justifyContent: "space-between", width: "100%" }}
                    >
                      <Space direction="vertical" size={2}>
                        <Text strong style={{ fontSize: isMobile ? 14 : 16 }}>
                          {name}
                        </Text>
                        <Text
                          type="secondary"
                          style={{ fontSize: isMobile ? 12 : 13 }}
                        >
                          Requested On: {dateStr}
                        </Text>
                        {(isAlreadyRegister || isActiveUser) && (
                          <Space size={4} style={{ marginTop: 4 }}>
                            {isAlreadyRegister && <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>Registered</Tag>}
                            {isActiveUser && <Tag color="green" style={{ margin: 0, fontSize: 10 }}>Active</Tag>}
                          </Space>
                        )}
                      </Space>
                    </Space>
                  </Col>

                  <Col xs={24}>
                    <Row gutter={[8, 8]}>
                      <Col xs={8}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          City
                        </Text>
                        <div style={{ fontSize: 13 }}>{city}</div>
                      </Col>
                      <Col xs={8}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          State
                        </Text>
                        <div style={{ fontSize: 13 }}>{state}</div>
                      </Col>
                      <Col xs={8}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Pincode
                        </Text>
                        <div style={{ fontSize: 13 }}>{pinCode}</div>
                      </Col>
                      <Col xs={24}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Contact
                        </Text>
                        <div style={{ fontSize: 13 }}>
                          <Text copyable={phone ? { text: phone } : false}>
                            {phone || "-"}
                          </Text>
                        </div>
                      </Col>
                    </Row>
                  </Col>

                  <Col xs={24}>
                    <Space
                      style={{ width: "100%", justifyContent: "flex-end" }}
                    >
                      <Button
                        icon={<PhoneOutlined />}
                        size={isMobile ? "middle" : "large"}
                        onClick={(e) => {
                          e.stopPropagation();
                          openDialer(item?.ContactNo);
                        }}
                      >
                        Call
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Card>
              </List.Item>
            );
          }}
        />
      )}
      {!loading && filtered.length > visibleUsers.length && (
        <div style={{ textAlign: "center", margin: "12px 0 24px" }}>
          <Button onClick={loadMore} size={isMobile ? "middle" : "large"}>
            Load more
          </Button>
        </div>
      )}
    </div>
  );
};

export default DirectRegistrationRequests;
